import { useSelector } from 'react-redux';

import { CheckmarkSolidIcon, ChevronDownIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Menu } from '@interstate/components/Menu';
import { Switch } from '@interstate/components/Switch';
import { Tooltip } from '@interstate/components/Tooltip';
import { PaymentLoading, usePaymentGridToggle } from '@makemydeal/dr-dash-components';
import { CASH } from '@makemydeal/dr-platform-types';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import { DealTypeChangeNotice } from '../../dealTypeDropdown/DealTypeChangeNotice';
import { useDealTypeDropdown } from '../../dealTypeDropdown/useDealTypeDropdown';
import { getScenarioPaymentSummary } from './getScenarioPaymentSummary';
import {
    DEAL_TYPE_MENU_ID_PREFIX,
    DealHeaderRoot,
    DealHeaderTop,
    DealTypeTitle,
    DealTypeTrigger,
    MenuItemIcon,
    PaymentGridControl,
    PaymentSummaryRoot,
    SummaryAmount,
    SummaryDetails,
    SummaryUnit
} from './ScenarioDealHeader.style';
import { ScenarioSourceBadge } from './ScenarioSourceBadge';

const DEAL_TYPE_MENU_WIDTH = '12.5rem';
const PAYMENT_GRID_LABEL_SX = { '& .MuiInputLabel-root': { minWidth: 0, paddingRight: '0.25rem' } };

export const ScenarioDealHeader = ({ scopeId }: { scopeId: string }) => {
    const theme = useInterstateTheme();
    const isPrimaryScope = useSelector(scopedStateSelectors.getPrimaryScopeId) === scopeId;
    const dealType = useDealTypeDropdown();
    const { currentOfferType } = dealType.labelParams;
    const paymentGrid = usePaymentGridToggle({ currentOfferType });
    const summary = getScenarioPaymentSummary(dealType.labelParams);

    const selectedValue = dealType.selectedDealType.toLowerCase();
    const menuOptions = dealType.menuOptions.map((option) => ({
        ...option,
        icon: (
            <MenuItemIcon theme={theme} aria-hidden>
                {option.value === selectedValue && <CheckmarkSolidIcon />}
            </MenuItemIcon>
        )
    }));
    const selectedLabel = menuOptions.find((option) => option.value === selectedValue)?.label ?? dealType.selectedDealType;

    const paymentGridSwitch = (
        <Switch
            name={`${paymentGrid.id}-${scopeId}`}
            checked={paymentGrid.isChecked}
            label={paymentGrid.label}
            aria-label={paymentGrid.label}
            labelSize="small"
            labelSx={PAYMENT_GRID_LABEL_SX}
            labelAlignment="left"
            layout="horizontal"
            onClick={paymentGrid.onToggle}
            disabled={paymentGrid.isDisabled}
            data-testid="scenario-payment-grid-toggle"
        />
    );

    return (
        <DealHeaderRoot data-testid="scenario-deal-header">
            <DealHeaderTop>
                <DealTypeTitle>
                    <DealTypeTrigger
                        type="button"
                        theme={theme}
                        aria-haspopup="menu"
                        aria-expanded={dealType.menuReferenceProps.open}
                        onClick={dealType.openMenu}
                        disabled={dealType.isLocked}
                        data-testid="scenario-deal-type-trigger"
                    >
                        {selectedLabel}
                        <ChevronDownIcon aria-hidden />
                    </DealTypeTrigger>
                    <ScenarioSourceBadge />
                </DealTypeTitle>
                {currentOfferType !== CASH && (
                    <PaymentGridControl>
                        {paymentGrid.gatedReason ? (
                            <Tooltip size="large" position="bottom" toolTipContent={paymentGrid.gatedReason}>
                                <span data-testid="scenario-payment-grid-gated">{paymentGridSwitch}</span>
                            </Tooltip>
                        ) : (
                            paymentGridSwitch
                        )}
                    </PaymentGridControl>
                )}
            </DealHeaderTop>
            {summary && (
                <PaymentSummaryRoot $isPrimary={isPrimaryScope} theme={theme} data-testid="scenario-payment-summary">
                    <PaymentLoading
                        testId="vpp-deal-type-dropdown-loading-indicator"
                        isLoading={dealType.isVPPTemplateProductsLoading}
                    />
                    <PaymentLoading
                        testId="payment-deal-type-dropdown-loading-indicator"
                        size={20}
                        opacity={0.8}
                        isLoading={dealType.isPaymentLoading}
                    />
                    <SummaryAmount>{summary.amount}</SummaryAmount>
                    <SummaryUnit theme={theme}>{summary.unit}</SummaryUnit>
                    <SummaryDetails theme={theme}>
                        {summary.details.map((detail) => (
                            <li key={detail}>{detail}</li>
                        ))}
                    </SummaryDetails>
                </PaymentSummaryRoot>
            )}
            <Menu
                id={`${DEAL_TYPE_MENU_ID_PREFIX}-${scopeId}`}
                width={DEAL_TYPE_MENU_WIDTH}
                referenceProps={dealType.menuReferenceProps}
                position="bottom-start"
                menuOptions={menuOptions}
            />
            {dealType.isDealTypeChangeNoticeShown && <DealTypeChangeNotice onClose={dealType.dismissDealTypeChangeNotice} />}
        </DealHeaderRoot>
    );
};
